import React, { useState, useEffect } from 'react';
import { Rnd, RndResizeCallback, RndDragCallback } from 'react-rnd';
import { useAppDispatch, useAppSelector } from '../../store';
import { 
  focusWindow, 
  updateWindowPosition,
  maximizeWindow,
  minimizeWindow,
  closeWindow,
  restoreWindow
} from '../../store/slices/windowsSlice';
import logger from '../../services/logger';

export interface WindowPanelProps {
  id: string;
  children: React.ReactNode;
  title: string;
  minWidth?: number;
  minHeight?: number;
  defaultWidth?: number;
  defaultHeight?: number;
  defaultX?: number;
  defaultY?: number;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  resizable?: boolean;
  draggable?: boolean;
  showControls?: boolean;
}

const WindowPanel: React.FC<WindowPanelProps> = ({
  id,
  children,
  title,
  minWidth = 200,
  minHeight = 200,
  defaultWidth = 600,
  defaultHeight = 400,
  defaultX = 100,
  defaultY = 100,
  className = '',
  contentClassName = '',
  headerClassName = '',
  resizable = true,
  draggable = true,
  showControls = true,
}) => {
  const dispatch = useAppDispatch();
  const windowState = useAppSelector(state => state.windows.windows[id]);
  const activeWindowId = useAppSelector(state => state.windows.activeWindowId);
  
  const [size, setSize] = useState({
    width: windowState?.position.width || defaultWidth,
    height: windowState?.position.height || defaultHeight,
  });
  
  const [position, setPosition] = useState({
    x: windowState?.position.x || defaultX,
    y: windowState?.position.y || defaultY,
  });

  useEffect(() => {
    if (windowState) {
      setSize({
        width: windowState.position.width,
        height: windowState.position.height,
      });
      setPosition({
        x: windowState.position.x,
        y: windowState.position.y,
      });
    }
  }, [windowState]);

  // Handle case when window is no longer in Redux store
  if (!windowState) {
    return null;
  }

  // If window is minimized, don't render it
  if (windowState.isMinimized) {
    return null;
  }

  const handleFocus = () => {
    if (id !== activeWindowId) {
      dispatch(focusWindow(id));
      logger.debug(`Window focused: ${title}`, { module: 'windows' });
    }
  };

  const handleDragStop: RndDragCallback = (e, d) => {
    const newPosition = {
      x: d.x,
      y: d.y,
    };
    setPosition(newPosition);
    dispatch(updateWindowPosition({ id, position: newPosition }));
    dispatch(focusWindow(id));
    logger.debug(`Window moved: ${title}`, { module: 'windows', data: newPosition });
  };

  const handleResizeStop: RndResizeCallback = (e, direction, ref, delta, position) => {
    const newSize = {
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    };
    const newPosition = {
      x: position.x,
      y: position.y,
    };
    
    setSize(newSize);
    setPosition(newPosition);
    
    dispatch(
      updateWindowPosition({
        id,
        position: {
          ...newPosition,
          width: newSize.width,
          height: newSize.height,
        },
      })
    );
    dispatch(focusWindow(id));
    logger.debug(`Window resized: ${title}`, { 
      module: 'windows', 
      data: { size: newSize, position: newPosition } 
    });
  };

  const handleMaximize = () => {
    if (windowState.isMaximized) {
      dispatch(restoreWindow(id));
    } else {
      dispatch(maximizeWindow(id));
    }
    logger.debug(`Window ${windowState.isMaximized ? 'restored' : 'maximized'}: ${title}`, {
      module: 'windows'
    });
  };

  const handleMinimize = () => {
    dispatch(minimizeWindow(id));
    logger.debug(`Window minimized: ${title}`, { module: 'windows' });
  };

  const handleClose = () => {
    dispatch(closeWindow(id));
    logger.debug(`Window closed: ${title}`, { module: 'windows' });
  };

  const isActive = id === activeWindowId;
  const isMaximized = windowState.isMaximized;

  return (
    <Rnd
      className={`rounded-md overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${
        isActive ? 'ring-2 ring-blue-500' : ''
      } ${className}`}
      style={{ zIndex: windowState.position.zIndex }}
      size={isMaximized ? { width: '100%', height: '100%' } : size}
      position={isMaximized ? { x: 0, y: 0 } : position}
      onDragStart={handleFocus}
      onResizeStart={handleFocus}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      minWidth={minWidth}
      minHeight={minHeight}
      disableDragging={!draggable || isMaximized}
      enableResizing={resizable && !isMaximized}
      dragHandleClassName="window-drag-handle"
    >
      <div className="flex flex-col h-full">
        {/* Window Header */}
        <div 
          className={`window-drag-handle flex justify-between items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 ${headerClassName}`}
        >
          <div className="font-medium truncate mr-2">{title}</div>
          {showControls && (
            <div className="flex space-x-2">
              <button
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none"
                onClick={handleMinimize}
                aria-label="Minimize"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                </svg>
              </button>
              <button
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none"
                onClick={handleMaximize}
                aria-label={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                  </svg>
                )}
              </button>
              <button
                className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 focus:outline-none"
                onClick={handleClose}
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Window Content */}
        <div className={`flex-1 overflow-auto p-4 ${contentClassName}`}>
          {children}
        </div>
      </div>
    </Rnd>
  );
};

export default WindowPanel; 